
import Vue from 'vue';
import { CourseModel } from '@descomplica/dex-models';
import { InformationModal } from '@descomplica/vue-components';
import Breakpoints from './Breakpoints.vue';
import { StudyPlanningBreakpointModel } from '@/models/studyPlanning/StudyPlanningBreakpointModel';
import { showPostgradErrorToast } from '@/helpers/postgradErrorToast';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { ChimeraCourseModel } from '@/models/chimera/ChimeraCourseModel';
import { PostgradTrialStatus } from '@/models/postgradTrial/PostgradTrialModel';
import { PostgradEarlyGraduationTrackingInformation } from '@/models/postgradEarlyGraduation/PostgradEarlyGraduationModel';
import { BUTTON_TEXT, EDITION_MODAL_INFO_TITLE } from '@/helpers/postgradStudyPlanning/constants';
import { TranslateResult } from 'vue-i18n';
import { StudyPlanningModel } from '@/models/studyPlanning/StudyPlanningModel';

interface ModalTexts {
  infoTitle: string;
  buttonLabel: string;
}

interface BreakpointOption {
  label: string;
  numberOfMonths: number;
  isPossible: boolean;
}

enum FOOTER_MESSAGES_ENUM {
  POSSIBLE = 'postgradStudyPlanningConfigModalSelectedBreakpointMessage',
  NOT_POSSIBLE = 'postgradStudyPlanningConfigModalNoPlanningOptions',
  TRIAL = 'postgradStudyPlanningConfigModalForTrial',
  NOT_SELECTED = 'postgradStudyPlanningConfigModalNoBreakpointMessage',
}

interface FooterMessage {
  i18nMessagePath: FOOTER_MESSAGES_ENUM;
  isDangerLine: boolean;
}

interface Data {
  selectedNumberOfMonths: number | string;
  isSavingStudyPlanning: boolean;
}

export default Vue.extend({
  components: {
    InformationModal,
    Breakpoints,
  },
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
  },
  data(): Data {
    return {
      selectedNumberOfMonths: 0,
      isSavingStudyPlanning: false,
    };
  },
  computed: {
    selectedBreakpoint(): StudyPlanningBreakpointModel | undefined {
      const chosenOption = this.studyPlanningBreakpoints.find(
        (b) => b.numberOfMonths === this.selectedNumberOfMonths,
      );

      return chosenOption;
    },
    studyPlanningModalTitle(): TranslateResult {
      if (this.userHasActiveStudyPlanning) {
        return this.$t('alterStudyPlanning');
      }

      return this.$t('studyPlanning');
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    currentChimeraCourse(): ChimeraCourseModel {
      return this.$store.getters.getCurrentChimeraCourse;
    },
    userHasActiveStudyPlanning(): boolean {
      return Boolean(this.$store.getters.studyPlanning(this.currentCourse.id));
    },
    configModalTexts(): ModalTexts {
      return {
        infoTitle: EDITION_MODAL_INFO_TITLE,
        buttonLabel: BUTTON_TEXT,
      };
    },
    userStudyPlanning(): StudyPlanningModel | null {
      return this.$store.getters.studyPlanning(this.currentCourse.id);
    },
    isUserInTrialCourse(): boolean {
      return !!this.$store.getters.trialByDescomplicaCourseId(this.currentChimeraCourse._id);
    },
    getTrialStatusByDescomplicaCourseId(): PostgradTrialStatus {
      return this.$store.getters.getTrialStatusByDescomplicaCourseId(
        this.currentChimeraCourse?._id,
      );
    },
    studyPlanningBreakpoints(): StudyPlanningBreakpointModel[] {
      return this.$store.getters.studyPlanningBreakpoints;
    },
    formattedStudyplanningExpirationDate(): string {
      if (!this.selectedBreakpoint && this.userHasActiveStudyPlanning) {
        return this.userStudyPlanning!.expirationDate.toLocaleDateString('pt-br');
      }

      if (!this.selectedBreakpoint) {
        return '';
      }

      return new Date(this.selectedBreakpoint.estimatedExpirationDate!).toLocaleDateString('pt-br');
    },
    breakpointsOptions(): BreakpointOption[] {
      return this.studyPlanningBreakpoints.map((b) => ({
        label: `${b.numberOfMonths} ${this.$tc(
          'postgradStudyPlanningConfigModalMonth',
          b.numberOfMonths,
        )}`,
        numberOfMonths: b.numberOfMonths,
        isPossible: b.isPossible,
      }));
    },
    isUnableToCreateStudyPlanning(): boolean {
      if (!this.studyPlanningBreakpoints?.length) return true;

      return !this.studyPlanningBreakpoints.some((breakpoint) => breakpoint.isPossible);
    },
    footerMessage(): FooterMessage {
      if (this.isUserInTrialCourse)
        return {
          i18nMessagePath: FOOTER_MESSAGES_ENUM.TRIAL,
          isDangerLine: false,
        };

      if (this.isUnableToCreateStudyPlanning)
        return {
          i18nMessagePath: FOOTER_MESSAGES_ENUM.NOT_POSSIBLE,
          isDangerLine: true,
        };

      if (this.userHasActiveStudyPlanning && !this.selectedBreakpoint)
        return {
          i18nMessagePath: FOOTER_MESSAGES_ENUM.POSSIBLE,
          isDangerLine: false,
        };

      if (!this.selectedBreakpoint)
        return {
          i18nMessagePath: FOOTER_MESSAGES_ENUM.NOT_SELECTED,
          isDangerLine: false,
        };

      return {
        i18nMessagePath: FOOTER_MESSAGES_ENUM.POSSIBLE,
        isDangerLine: false,
      };
    },
    earlyGraduationTrackingProps(): PostgradEarlyGraduationTrackingInformation {
      return this.$store.getters.earlyGraduationTrackings(this.currentChimeraCourse);
    },
  },
  created() {
    Analytics.trackEvent(EVENT_LABELS.modalViewed, {
      in_postgrad_study_planning_config_modal: true,
      with_breakpoints: true,
      course: this.currentCourse.name,
      category: 'pos',
      ...this.getTrialStatusByDescomplicaCourseId,
      ...this.earlyGraduationTrackingProps,
    });
  },
  methods: {
    selectOption(numberOfMonths: number | string): void {
      if (numberOfMonths === this.selectedNumberOfMonths) {
        this.selectedNumberOfMonths = 0;
        return;
      }

      Analytics.trackEvent(EVENT_LABELS.postgradStudyPlanningOptionChosen, {
        course: this.currentCourse.name,
        chosen_months_option: numberOfMonths,
        ...this.getTrialStatusByDescomplicaCourseId,
        ...this.earlyGraduationTrackingProps,
      });

      this.selectedNumberOfMonths = numberOfMonths;
    },
    closeModal(): void {
      Analytics.trackEvent(EVENT_LABELS.modalClosed, {
        course: this.currentCourse.name,
        postgrad_study_planning: true,
        ...this.getTrialStatusByDescomplicaCourseId,
        ...this.earlyGraduationTrackingProps,
      });

      this.$emit('onClose');
    },
    async saveStudyPlanning(): Promise<void> {
      this.isSavingStudyPlanning = true;

      try {
        const { userHasActiveStudyPlanning } = this;

        await this.$store.dispatch('SAVE_STUDY_PLANNING', {
          courseId: this.currentCourse.id,
          numberOfMonths: this.selectedNumberOfMonths,
        });

        this.$emit('studyPlanningSaved');

        Analytics.trackEvent(EVENT_LABELS.postgradStudyPlanningSaved, {
          course: this.currentCourse.name,
          is_editing_postgrad_study_planning: userHasActiveStudyPlanning,
          chosen_months_option: this.selectedNumberOfMonths,
          ...this.getTrialStatusByDescomplicaCourseId,
          ...this.earlyGraduationTrackingProps,
        });
      } catch (err) {
        showPostgradErrorToast(err as Error, this.$t('saveStudyPlanningError').toString());

        Analytics.trackEvent(EVENT_LABELS.savePostgradStudyPlanningError, {
          course: this.currentCourse.name,
          ...this.getTrialStatusByDescomplicaCourseId,
        });
      }

      this.isSavingStudyPlanning = false;
      this.closeModal();
    },
  },
});
